import { useCallback, useEffect } from "react";
import {
  setMixpanelOnAccept,
  setMixpanelOnDecline,
} from "lib/mixpanel/mixpanel";

type CookiebotEvent =
  | "CookiebotOnConsentReady"
  | "CookiebotOnLoad"
  | "CookiebotOnAccept"
  | "CookiebotOnDecline"
  | "CookiebotOnDialogInit"
  | "CookiebotOnDialogDisplay"
  | "CookiebotOnTagsExecuted";

/**
 * Visit https://www.cookiebot.com/en/developer#h-event-handling
 * for info on callbacks
 */
export const useCookiebotCallback = (
  event: CookiebotEvent,
  fn: EventListenerOrEventListenerObject
) => {
  useEffect(() => {
    if (!window.Cookiebot) return;

    window.addEventListener(event, fn);

    return () => {
      window.removeEventListener(event, fn);
    };
  }, [event, fn]);
};

/**
 * This set-up ensures that on every page load the Mixpanel preferences are updated
 */
export const useCookiebot = () => {
  const handleCookiebot = useCallback(() => {
    window.addEventListener("CookiebotOnAccept", setMixpanelOnAccept);
    window.addEventListener("CookiebotOnDecline", setMixpanelOnDecline);
  }, []);

  const cleanUpCookiebot = useCallback(() => {
    window.removeEventListener("CookiebotOnAccept", setMixpanelOnAccept);
    window.removeEventListener("CookiebotOnDecline", setMixpanelOnDecline);
  }, []);

  useEffect(() => {
    handleCookiebot();
    return () => {
      cleanUpCookiebot();
    };
  }, [handleCookiebot, cleanUpCookiebot]);
};
